import { getUID } from '@redviking/argonaut-util/src/getUID';
import type { NodeCfg, NodeSocketKind, WorkOrderNodeOutputs } from '@redviking/argonaut-util/types/mes/applet-designs/flow.latest';
import { accessor } from 'src/store';
import { defineControlOpts } from '../../controls/FlowNodeControl';
import type { NodeSpec } from './NodeSpec';

const workOrderColumnLabels: Record<WorkOrderNodeOutputs, string> = {
    orderNumber: 'Name',
    status: 'Status',
    pendingCount: 'Pending Count',
    totalCount: 'Total Count',
    inProgressCount: 'In Progress Count',
    scrappedCount: 'Scrapped Count',
    completedCount: 'Completed Count',
    targetQuantity: 'Target Quantity',
    incompleteCount: 'Incomplete Count',
    rawMaterialData: 'Raw Material Data',
    quarantinedCount: 'Quarantined Count',
};

const outputItems = Object.entries(workOrderColumnLabels).map(([ value, label ]) => ({ value, label }));

export const workOrderUiNodeSpec: NodeSpec<NodeCfg<'workOrder'>> = {
    kindLabel: 'Work Order',
    nodeCategory: 'argonautApi',
    requiredSockets: [ 'workOrderId', 'watchWorkOrder' ],
    controls: nodeCfg => {
        const availableOutputs = outputItems.filter(item => !nodeCfg.controlValues.subcribedColumns[item.value as keyof typeof nodeCfg.controlValues.subcribedColumns]);
        if (!availableOutputs.length) {
            return {};
        }
        return {
            subcribedColumns: defineControlOpts({
                cfg: {
                    kind: 'selectWithCallback',
                    selectLabel: 'Add Output',
                    items: availableOutputs,
                },
                on: {
                    'update:value': () => {}, // no-op, select event is used instead
                    'select': (value, { flowEventEmitter }) => {
                        const key = value as keyof typeof nodeCfg.controlValues.subcribedColumns;
                        flowEventEmitter.emit('updateNodeCfg', { newNodeCfg: { ...nodeCfg, controlValues: { ...nodeCfg.controlValues, subcribedColumns: { ...nodeCfg.controlValues.subcribedColumns, [key]: true } } } });
                    },
                },
                value: nodeCfg.controlValues.subcribedColumns,
                enableWithConnectedSocket: true,
            }),
        };
    },
    cfg: () => ({
        disabled: false,
        id: getUID(),
        kind: 'workOrder',
        controlValues: {
            subcribedColumns: {
                status: true,
                orderNumber: false,
                pendingCount: false,
                totalCount: false,
                inProgressCount: false,
                targetQuantity: true,
                completedCount: true,
                scrappedCount: false,
                incompleteCount: true,
                rawMaterialData: false,
                quarantinedCount: false,
            },
        },
    }),
    inputs: () => ({
        watchWorkOrder: { socket: 'event', label: 'Watch Work Order' },
        workOrderId: { socket: 'string', label: 'Work Order ID' },
    }),
    outputs: nodeCfg => {
        const outputs: Record<string, { socket: NodeSocketKind; label?: string; controlOpts?: ReturnType<typeof defineControlOpts> }> = {
            workOrderUpdate: { socket: 'event', label: 'Work Order Update' },
        };
        for (const [ key, enabled ] of Object.entries(nodeCfg.controlValues.subcribedColumns)) {
            if (enabled) {
                const typedKey = key as keyof typeof workOrderColumnLabels;
                let socket: NodeSocketKind;
                if (typedKey === 'status') {
                    socket = 'string';
                } else if (typedKey === 'rawMaterialData') {
                    socket = 'workOrderMaterialData';
                } else {
                    socket = 'number';
                }
                outputs[typedKey] = {
                    socket,
                    controlOpts: defineControlOpts({
                        cfg: { kind: 'deletableSocket', label: workOrderColumnLabels[typedKey], rightAlign: true },
                        value: null,
                        enableWithConnectedSocket: true,
                        on: {
                            'update:value': () => {}, // no-op, this control does not use the update:value event
                            'delete': (_, { flowEventEmitter }) => flowEventEmitter.emit('removeNodeSocket', {
                                newNodeCfg: { ...nodeCfg, controlValues: { ...nodeCfg.controlValues, subcribedColumns: { ...nodeCfg.controlValues.subcribedColumns, [typedKey]: false } } },
                                direction: 'output',
                                removalMethod: { kind: 'uniqueIdentifier', value: typedKey },
                            }),
                        },
                    }),
                };
            }
        }
        return outputs as ReturnType<NodeSpec<NodeCfg<'workOrder'>>['outputs']>;
    },
    validator: node => {
        const subscribedItems = Object.values(node.controlValues.subcribedColumns).filter(Boolean);
        return subscribedItems.length ? true : { subcribedColumns: 'At least one output is required' };
    },
    suppressNode: () => !accessor.testFeatureFlagRequirements([ 'unstableWorkOrders' ]),
};
